import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Col, Dropdown, DropdownMenu, DropdownToggle, Row, Spinner } from "reactstrap";

import {
  getInstalledIntegrations,
  TenantIntegration,
} from "../../services/integrationService";

const APPS_PER_ROW = 3;

const providerIcon = (slug: string): string => {
  switch (slug) {
    case "asaas":
      return "ri-bank-card-line";
    case "iugis":
      return "ri-whatsapp-line";
    default:
      return "ri-plug-line";
  }
};

const formatProviderName = (slug: string): string =>
  slug
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const resolveHeaderApps = (
  integrations: TenantIntegration[]
): TenantIntegration[] => {
  const enabledByProvider = new Map<string, TenantIntegration>();

  integrations.forEach((integration) => {
    if (!integration.enabled) {
      return;
    }

    const pinToHeader = integration.settings?.pin_to_header;

    if (pinToHeader === false) {
      return;
    }

    if (!enabledByProvider.has(integration.provider_slug)) {
      enabledByProvider.set(integration.provider_slug, integration);
    }
  });

  return Array.from(enabledByProvider.values()).sort((left, right) =>
    left.provider_slug.localeCompare(right.provider_slug)
  );
};

const chunkApps = <T,>(items: T[], size: number): T[][] => {
  const rows: T[][] = [];

  for (let index = 0; index < items.length; index += size) {
    rows.push(items.slice(index, index + size));
  }

  return rows;
};

const WebAppsDropdown = () => {
  const { t } = useTranslation();
  const [isWebAppDropdown, setIsWebAppDropdown] = useState<boolean>(false);
  const [installedApps, setInstalledApps] = useState<TenantIntegration[]>([]);
  const [loading, setLoading] = useState(false);

  const headerApps = useMemo(
    () => resolveHeaderApps(installedApps),
    [installedApps]
  );

  const appRows = useMemo(
    () => chunkApps(headerApps, APPS_PER_ROW),
    [headerApps]
  );

  const fetchInstalledApps = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getInstalledIntegrations();
      setInstalledApps(response.data ?? []);
    } catch {
      setInstalledApps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInstalledApps();
  }, [fetchInstalledApps]);

  useEffect(() => {
    if (isWebAppDropdown) {
      fetchInstalledApps();
    }
  }, [isWebAppDropdown, fetchInstalledApps]);

  const toggleWebAppDropdown = () => {
    setIsWebAppDropdown((previous) => !previous);
  };

  const closeDropdown = () => {
    setIsWebAppDropdown(false);
  };

  return (
    <React.Fragment>
      <Dropdown
        isOpen={isWebAppDropdown}
        toggle={toggleWebAppDropdown}
        className="topbar-head-dropdown ms-1 header-item"
      >
        <DropdownToggle
          tag="button"
          type="button"
          className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
        >
          <i className="bx bx-category-alt fs-22"></i>
        </DropdownToggle>
        <DropdownMenu className="dropdown-menu-lg p-0 dropdown-menu-end">
          <div className="p-3 border-top-0 border-start-0 border-end-0 border-dashed border">
            <Row className="align-items-center">
              <Col>
                <h6 className="m-0 fw-semibold fs-15">
                  {t("header.web_apps.title")}
                </h6>
              </Col>
              <div className="col-auto">
                <Link
                  to="/settings/integrations"
                  className="btn btn-sm btn-soft-info"
                  onClick={closeDropdown}
                >
                  {t("header.web_apps.view_all")}
                  <i className="ri-arrow-right-s-line align-middle"></i>
                </Link>
              </div>
            </Row>
          </div>

          <div className="p-2">
            {loading && headerApps.length === 0 ? (
              <div className="py-4 text-center text-muted">
                <Spinner size="sm" className="me-2" />
                {t("header.web_apps.loading")}
              </div>
            ) : headerApps.length === 0 ? (
              <div className="py-4 px-3 text-center text-muted">
                <i className="bx bx-plug fs-24 d-block mb-2"></i>
                <p className="mb-3 fs-13">{t("header.web_apps.empty")}</p>
                <Link
                  to="/settings/integrations"
                  className="btn btn-sm btn-soft-primary"
                  onClick={closeDropdown}
                >
                  {t("header.web_apps.explore")}
                  <i className="ri-arrow-right-s-line align-middle ms-1"></i>
                </Link>
              </div>
            ) : (
              appRows.map((row, rowIndex) => (
                <div key={`apps-row-${rowIndex}`} className="row g-0">
                  {row.map((integration) => (
                    <Col key={integration.id}>
                      <Link
                        className="dropdown-icon-item"
                        to="/settings/integrations"
                        onClick={closeDropdown}
                      >
                        <span className="avatar-xs d-inline-block mb-1">
                          <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-16">
                            <i
                              className={providerIcon(integration.provider_slug)}
                            ></i>
                          </span>
                        </span>
                        <span>{formatProviderName(integration.provider_slug)}</span>
                      </Link>
                    </Col>
                  ))}
                </div>
              ))
            )}
          </div>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default WebAppsDropdown;
